import { useState, useEffect, useCallback } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { AlertTriangle, MessageSquare, Check } from 'lucide-react'

interface ErrorDetails {
  type: string
  message: string
  stack?: string
  source?: string
  line?: number
  column?: number
  url: string
  timestamp: string
}

/**
 * ErrorDialog - Shows a dialog when runtime errors occur (button clicks, etc.)
 *
 * Unlike ErrorBoundary (which catches React render errors), this catches:
 * - Event handler errors (button clicks, form submissions)
 * - Unhandled promise rejections
 *
 * Includes "Send to Chat" button for Cayu AI integration.
 */
export function ErrorDialog() {
  const [open, setOpen] = useState(false)
  const [error, setError] = useState<ErrorDetails | null>(null)
  const [sent, setSent] = useState(false)

  // Listen for error events from error-telemetry
  useEffect(() => {
    const handleError = (event: CustomEvent<ErrorDetails>) => {
      setError(event.detail)
      setOpen(true)
      setSent(false)
    }

    window.addEventListener('frontend-error', handleError as EventListener)
    return () => {
      window.removeEventListener('frontend-error', handleError as EventListener)
    }
  }, [])

  const formatErrorForClaude = useCallback((): string => {
    if (!error) return ''

    let message = `## Frontend Error\n\n`
    message += `**Error:** ${error.message}\n\n`
    message += `**Type:** ${error.type}\n\n`
    message += `**URL:** ${error.url}\n\n`

    if (error.source) {
      message += `**Source:** ${error.source}:${error.line}:${error.column}\n\n`
    }

    if (error.stack) {
      const shortStack = error.stack.split('\n').slice(0, 6).join('\n')
      message += `**Stack Trace:**\n\`\`\`\n${shortStack}\n\`\`\``
    }

    return message
  }, [error])

  const handleSendToChat = useCallback(() => {
    const errorText = formatErrorForClaude()

    try {
      // Send to parent window (Cayu AI)
      window.parent.postMessage({
        type: 'cayu:error',
        payload: {
          message: errorText,
          timestamp: Date.now()
        }
      }, '*')

      setSent(true)
      setTimeout(() => {
        setOpen(false)
      }, 1500)
    } catch (err) {
      console.warn('Failed to send error to chat:', err)
    }
  }, [formatErrorForClaude])

  if (!error) return null

  const isDev = import.meta.env.DEV

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
              <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
            </div>
            <div>
              <DialogTitle className="text-red-800 dark:text-red-200">
                Something went wrong
              </DialogTitle>
              <DialogDescription className="text-red-600 dark:text-red-400">
                An error occurred while performing this action
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="mt-4 space-y-4">
          {/* Error Message */}
          <div className="rounded-md bg-red-50 dark:bg-red-900/20 p-4">
            <p className="text-sm text-red-800 dark:text-red-200 break-words">
              {error.message}
            </p>
          </div>

          {/* Technical Details (Dev Mode Only) */}
          {isDev && error.stack && (
            <details className="group">
              <summary className="cursor-pointer text-sm text-muted-foreground hover:text-foreground">
                Technical details
              </summary>
              <pre className="mt-2 rounded-md bg-muted p-3 text-xs overflow-auto max-h-32 whitespace-pre-wrap break-all">
                {error.stack}
              </pre>
            </details>
          )}
        </div>

        <DialogFooter className="mt-6">
          <Button
            onClick={handleSendToChat}
            className="w-full sm:w-auto bg-red-600 hover:bg-red-700 text-white"
          >
            {sent ? (
              <>
                <Check className="mr-2 h-4 w-4" />
                Sent!
              </>
            ) : (
              <>
                <MessageSquare className="mr-2 h-4 w-4" />
                Send to Chat
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
